import clsx from 'clsx';

import Flex from '@commons/containers/Flex';
import ResponsiveContainer from '@commons/containers/ResponsiveContainer';
import PageBuilder from '@commons/pagebuilder';
import type { DrupalSection } from '@commons/types/drupal';
import type { Alignment, Breakpoint, Spacing, Theme } from '@commons/types/styles';

import { type SectionMinHeightType, getSectionMinHeight } from '../helpers';
import Region from './Region';
import styles from './index.module.scss';

// Regions of the tiles section.

const REGIONS = ['startTile', 'endTile'] as const;

// Type definition for the settings of a tile region.

type RegionSettings = {
  theme: Theme;
  horizontalAlignment: Alignment;
  verticalAlignment: Alignment;
};

// Type definition for the TilesSection component properties.

type Props = DrupalSection<{
  container: {
    minHeightType: SectionMinHeightType;
    customMinHeight?: string;
  };
  layout: {
    padding: Spacing;
    mobileBreakpoint: Breakpoint;
  };
  regions: {
    startTile: RegionSettings;
    endTile: RegionSettings;
  };
}>;

/**
 * Tiles section component.
 *
 * IMPORTANT:
 * - This component should be a server component, otherwise everything it renders and itself will be client components.
 *   Since it is used on many pages, this would bloat the JavaScript bundle significantly.
 */

const TilesSection = ({ type, blocks, weight, settings }: Props) => {
  const { container, layout, regions } = settings;

  const { minHeightType, customMinHeight } = container;
  const { padding, mobileBreakpoint } = layout;

  const minHeight = getSectionMinHeight(minHeightType, customMinHeight);

  const sectionData = { type, weight };
  const blocksByRegions = PageBuilder.filterBlocksByRegions(blocks, REGIONS as unknown as string[]);

  const renderedRegions = REGIONS.map((region) => {
    const renderedBlocks = blocksByRegions[region].map(PageBuilder.renderBlock);
    const { theme, horizontalAlignment, verticalAlignment } = regions[region];

    return PageBuilder.renderRegion(
      region,
      Region,
      {
        theme,
        width: { xs: 12, [mobileBreakpoint]: 6 },
        align: verticalAlignment,
        justify: horizontalAlignment,
        padding,
        children: renderedBlocks,
      },
      sectionData
    );
  });

  return (
    <ResponsiveContainer
      paddingTop="none"
      paddingBottom="none"
      horizontalAlignment="center"
      className={styles.section}
      style={{ minHeight }}
    >
      <Flex
        width={12}
        align="stretch"
        direction={{ xs: 'column', [mobileBreakpoint]: 'row' }}
        className={clsx(styles.regions, styles[`mobile-${mobileBreakpoint}`])}
      >
        {renderedRegions}
      </Flex>
    </ResponsiveContainer>
  );
};

export default TilesSection;
